import React, { FC, useMemo } from 'react';
import Chip, { ChipProps } from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';

import { CheckFilledIcon, CloseFilledIcon, HorizontalHalfMoonIcon, DottedCircleIcon } from 'src/shared/components';
import { TimesheetStatusEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';

interface Props {
  status: TimesheetStatusEnums | undefined;
}

export const TimesheetStatus: FC<Props> = ({ status }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const statusProps = useMemo<ChipProps>(() => {
    const isDark = theme.palette.mode === 'dark';

    switch (status) {
      case TimesheetStatusEnums.OPEN:
        return {
          label: t('global.open'),
          icon: <DottedCircleIcon size={16} />,
          sx: {
            border: isDark ? 'solid 1px white' : 'solid 1px rgba(0, 0, 0, 0.08)',
          },
        };
      case TimesheetStatusEnums.COMPLETED:
        return {
          label: t('global.completed'),
          icon: <HorizontalHalfMoonIcon size={20} />,
          sx: {
            backgroundColor: isDark ? undefined : '#fbf5df !important',
            color: '#e37f00',
            border: isDark ? 'solid 1px #e37f00' : undefined,
          },
        };
      case TimesheetStatusEnums.APPROVED:
        return {
          label: t('absences.approved'),
          icon: <CheckFilledIcon color="#83b72c" size={20} />,
          sx: {
            backgroundColor: isDark ? undefined : '#f3f8e7 !important',
            color: '#83b72c',
            border: isDark ? 'solid 1px #83b72c' : undefined,
          },
        };
      case TimesheetStatusEnums.REJECTED:
        return {
          label: t('timesheets.rejected'),
          icon: <CloseFilledIcon color="#b92c28" />,
          sx: {
            backgroundColor: isDark ? undefined : '#fdebed !important',
            color: '#b92c28',
            border: isDark ? 'solid 1px #b92c28' : undefined,
          },
        };
      default:
        return {};
      // No default
    }
  }, [status, theme]);

  return <Chip {...statusProps} sx={{ ...statusProps?.sx, borderRadius: 2 }} />;
};
